import React from 'react';
import { formatUsd } from '@/lib/estimatesData';
import { FileSignature, ScrollText, AlertTriangle } from 'lucide-react';

/* ============================================================================
 * ProposalAcceptance — the signable contract pages appended to a proposal.
 *
 *   1. Terms & Conditions        (only when legal add-on is on AND a state pack
 *                                 exists; otherwise a "configure your state"
 *                                 placeholder)
 *   2. Acceptance page           (summary + red cancellation banner + owner /
 *                                 contractor signature lines + consumer note)
 *   3. Notice of Cancellation    (statutory owner + contractor copies)
 *
 * All render-only + print-ready (each major page starts on a fresh sheet).
 * ========================================================================== */

const SignatureLine = ({ label }) => (
  <div className="pt-8">
    <div className="border-t border-foreground/70" />
    <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
      {label}
    </div>
  </div>
);

const SummaryRow = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 px-4 py-2.5 border-t border-border first:border-t-0">
    <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground shrink-0 pt-0.5">
      {label}
    </span>
    <span className="text-sm text-foreground text-right">{children}</span>
  </div>
);

const CancellationCopy = ({ pack, sellerLine, title }) => (
  <section className="print-break-before pt-2" data-testid="preview-cancellation">
    <div className="flex items-center gap-2 mb-1">
      <AlertTriangle size={16} className="text-[hsl(var(--primary))]" />
      <h2 className="text-lg font-semibold text-[hsl(var(--primary))]">
        {title}
      </h2>
    </div>
    <p className="text-[12px] italic text-muted-foreground mb-4">
      {pack.cancellation.intro}
    </p>
    <div className="text-[11px] leading-relaxed text-foreground/90 space-y-3">
      <div>Date of transaction: _______________________________</div>
      {pack.cancellation.paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
      <p>{pack.cancellation.howToCancel.replace('{seller}', sellerLine)}</p>
      <p className="font-semibold pt-1">{pack.cancellation.acknowledgment}</p>
    </div>
    <div className="grid grid-cols-2 gap-8 mt-8">
      <SignatureLine label="Date" />
      <SignatureLine label="Buyer’s signature" />
    </div>
  </section>
);

export const ProposalAcceptance = ({
  estimate,
  totals,
  company,
  client,
  pack,
  projectAddress,
}) => {
  const legalOn = !!estimate?.include_legal;
  const isTM = estimate?.layout === 'tm';
  const tm = estimate?.tm_terms || {};
  const brandName = company?.name || 'K.O. CO+RE SYSTEMS';
  const clientName = client?.name || estimate?.client_name || 'Client';
  const schedule = totals?.payment_schedule || [];
  const scheduleSummary = schedule.length
    ? schedule.map((r) => formatUsd(r.amount)).join('  /  ')
    : '—';
  const sellerLine = [
    brandName,
    company?.address || company?.mailing_address || '',
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      {/* 1) TERMS & CONDITIONS ------------------------------------------- */}
      {legalOn && pack ? (
        <section
          className="print-break-before pt-2"
          data-testid="preview-terms-conditions"
        >
          <div className="flex items-center gap-2 mb-3">
            <ScrollText size={16} className="text-[hsl(var(--primary))]" />
            <h2 className="text-lg font-semibold text-[hsl(var(--primary))]">
              Terms and Conditions
            </h2>
          </div>
          <p className="text-[12px] italic text-muted-foreground mb-4">
            These terms are incorporated into and made part of this proposal.
          </p>
          <ol className="space-y-3">
            {pack.terms.map((term, i) => (
              <li key={i} className="print-avoid">
                <div className="text-sm font-semibold text-foreground">
                  {i + 1}. {term.title}
                </div>
                <p className="mt-0.5 text-[12.5px] leading-relaxed text-foreground/85">
                  {term.body}
                </p>
              </li>
            ))}
          </ol>
          {pack.consumerNote ? (
            <div className="mt-5 rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/[0.05] px-4 py-3 print-avoid">
              <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--primary))]">
                {pack.consumerNote.title}
              </div>
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-foreground/85">
                {pack.consumerNote.body}
              </p>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Legal requested but no verified pack for this state. */}
      {legalOn && !pack ? (
        <section
          className="print-break-before pt-2"
          data-testid="preview-legal-placeholder"
        >
          <div className="rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 px-4 py-4">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 font-semibold">
              <AlertTriangle size={15} />
              Contract terms not configured for this state
            </div>
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-amber-900/80 dark:text-amber-100/80">
              A verified Terms &amp; Conditions and cancellation-notice pack
              isn’t set up for the project’s state yet. Set the project address
              state to a configured state (currently Illinois), or ask to add
              your state’s wording. The acceptance &amp; signature page below
              still prints.
            </p>
          </div>
        </section>
      ) : null}

      {/* 2) ACCEPTANCE PAGE --------------------------------------------- */}
      <section
        className="print-break-before pt-2"
        data-testid="preview-acceptance"
      >
        <div className="flex items-center gap-2 mb-3">
          <FileSignature size={16} className="text-[hsl(var(--primary))]" />
          <h2 className="text-lg font-semibold text-[hsl(var(--primary))]">
            Proposal Acceptance
          </h2>
        </div>
        <p className="text-[12px] italic text-muted-foreground mb-4">
          Acceptance authorizes {brandName} to proceed according to this
          proposal, final drawings and approved written changes.
        </p>

        <div className="rounded-md border border-border overflow-hidden">
          <SummaryRow label="Project">
            {estimate?.project_name || 'Project'}
          </SummaryRow>
          {projectAddress ? (
            <SummaryRow label="Project address">{projectAddress}</SummaryRow>
          ) : null}
          {isTM ? (
            <>
              <SummaryRow label="Basis">Time &amp; Material</SummaryRow>
              {tm.hourly_rate ? (
                <SummaryRow label="Hourly rate">
                  <span className="tabular-nums">
                    {/^[\d.,]+$/.test(String(tm.hourly_rate))
                      ? `$${tm.hourly_rate}`
                      : tm.hourly_rate}{' '}
                    per hour
                  </span>
                </SummaryRow>
              ) : null}
              {tm.deposit ? (
                <SummaryRow label="Deposit / retainer">
                  <span className="tabular-nums">
                    {/^[\d.,]+$/.test(String(tm.deposit))
                      ? `$${tm.deposit}`
                      : tm.deposit}
                  </span>
                </SummaryRow>
              ) : null}
            </>
          ) : (
            <>
              <SummaryRow label="Total investment">
                <span className="font-semibold tabular-nums">
                  {formatUsd(totals?.total)}
                </span>
              </SummaryRow>
              {schedule.length ? (
                <SummaryRow label="Payment schedule">
                  <span className="tabular-nums">{scheduleSummary}</span>
                </SummaryRow>
              ) : null}
            </>
          )}
          {estimate?.proposal_valid_for ? (
            <SummaryRow label="Proposal valid for">
              {estimate.proposal_valid_for}
            </SummaryRow>
          ) : null}
        </div>

        {/* Red cancellation-rights banner (legal + pack only). */}
        {legalOn && pack?.cancellationBanner ? (
          <div className="mt-5 border-2 border-[hsl(var(--destructive))]/60 bg-[hsl(var(--destructive))]/[0.06] px-4 py-3 text-center print-avoid">
            <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[hsl(var(--destructive))] leading-relaxed">
              {pack.cancellationBanner}
            </p>
          </div>
        ) : null}

        <p className="mt-5 text-[12px] leading-relaxed text-foreground/80">
          The undersigned owner(s) acknowledge that they have reviewed and
          accept the scope, pricing, payment schedule, exclusions,
          responsibilities and terms contained in this proposal.
        </p>

        {/* Owner acceptance */}
        <div className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Owner Acceptance
        </div>
        <div className="grid grid-cols-2 gap-8">
          <SignatureLine label={`${clientName} — Owner Signature`} />
          <SignatureLine label="Date" />
          <SignatureLine label="Owner Signature" />
          <SignatureLine label="Date" />
        </div>

        {/* Contractor acceptance */}
        <div className="mt-8 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Contractor Acceptance
        </div>
        <div className="grid grid-cols-2 gap-8">
          <SignatureLine label={`${brandName}`} />
          <SignatureLine label="Date" />
        </div>
      </section>

      {/* 3) NOTICE OF CANCELLATION — owner + contractor copies ----------- */}
      {legalOn && pack?.cancellation ? (
        <>
          <CancellationCopy
            pack={pack}
            sellerLine={sellerLine}
            title={`${pack.cancellation.title} — Owner Copy`}
          />
          <CancellationCopy
            pack={pack}
            sellerLine={sellerLine}
            title={`${pack.cancellation.title} — Contractor Copy`}
          />
        </>
      ) : null}
    </>
  );
};

export default ProposalAcceptance;
